import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  Snackbar,
  Chip,
  Tabs,
  Tab,
  Badge,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Remove,
  Settings,
  Warning,
  CheckCircle,
  Error,
  Inventory,
  Send,
  History,
  CloudUpload,
} from '@mui/icons-material';
import { stockTechnicienAPI, transfertsAPI, alertesAPI } from '../../services/stockService';
import ImportCSVDialog from '../../components/stock/ImportCSVDialog';

const StockTechnicienPage = () => {
  const [monStock, setMonStock] = useState([]);
  const [stockPrincipal, setStockPrincipal] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [alertes, setAlertes] = useState([]);
  const [historique, setHistorique] = useState([]);
  const [infoStock, setInfoStock] = useState(null);
  const [openImportDialog, setOpenImportDialog] = useState(false);

  // Dialogs
  const [openSortieDialog, setOpenSortieDialog] = useState(false);
  const [openSeuilDialog, setOpenSeuilDialog] = useState(false);
  const [openTransfertDialog, setOpenTransfertDialog] = useState(false);
  
  // Form data
  const [selectedProduit, setSelectedProduit] = useState(null);
  const [formData, setFormData] = useState({
    quantite: '',
    motif: '',
    seuil_alerte: '',
    commentaire: ''
  });
  
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      if (tabValue === 0) {
        // Onglet Mon Stock
        const [stockRes, infoRes] = await Promise.all([
          stockTechnicienAPI.getMonStock(),
          stockTechnicienAPI.getInfo()
        ]);
        setMonStock(stockRes.data.data || []);
        setInfoStock(infoRes.data.data || {});
      } else if (tabValue === 1) {
        // Onglet Demander Réapprovisionnement
        const stockPrincipalRes = await stockTechnicienAPI.getStockPrincipal();
        setStockPrincipal(stockPrincipalRes.data.data || []);
      } else if (tabValue === 2) {
        // Onglet Alertes
        const alertesRes = await stockTechnicienAPI.getMesAlertes({ lu: false });
        setAlertes(alertesRes.data.data || []);
      } else if (tabValue === 3) {
        // Onglet Historique
        const historiqueRes = await stockTechnicienAPI.getMonHistorique({ limit: 50 });
        setHistorique(historiqueRes.data.data || []);
      }
    } catch (error) {
      showSnackbar('Erreur lors du chargement des données', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenSortieDialog = (produit) => {
    setSelectedProduit(produit);
    setFormData({ quantite: '', motif: '' });
    setOpenSortieDialog(true);
  };

  const handleOpenSeuilDialog = (produit) => {
    setSelectedProduit(produit);
    setFormData({ seuil_alerte: produit.seuil_alerte });
    setOpenSeuilDialog(true);
  };

  const handleOpenTransfertDialog = (produit) => {
    setSelectedProduit(produit);
    setFormData({ quantite: '', commentaire: '' });
    setOpenTransfertDialog(true);
  };

  const handleCloseDialogs = () => {
    setOpenSortieDialog(false);
    setOpenSeuilDialog(false);
    setOpenTransfertDialog(false);
    setSelectedProduit(null);
    setFormData({ quantite: '', motif: '', seuil_alerte: '', commentaire: '' });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSortieStock = async (e) => {
    e.preventDefault();
    try {
      await stockTechnicienAPI.sortieStock({
        id_medicament: selectedProduit.id_medicament,
        quantite: parseInt(formData.quantite),
        motif: formData.motif
      });
      showSnackbar('Sortie enregistrée avec succès', 'success');
      handleCloseDialogs();
      loadData();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Erreur lors de la sortie', 'error');
    }
  };

  const handleUpdateSeuil = async (e) => {
    e.preventDefault();
    try {
      await stockTechnicienAPI.updateMonSeuil(selectedProduit.id_medicament, {
        seuil_alerte: parseInt(formData.seuil_alerte)
      });
      showSnackbar('Seuil d\'alerte mis à jour', 'success');
      handleCloseDialogs();
      loadData();
    } catch (error) {
      showSnackbar('Erreur lors de la mise à jour', 'error');
    }
  };

  const handleDemanderTransfert = async (e) => {
    e.preventDefault();
    try {
      await transfertsAPI.demanderTransfert({
        id_stock_destination: infoStock.stock.id_stock,
        id_medicament: selectedProduit.id_medicament,
        quantite: parseInt(formData.quantite),
        commentaire: formData.commentaire
      });
      showSnackbar('Demande de transfert envoyée', 'success');
      handleCloseDialogs();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Erreur', 'error');
    }
  };

  const handleMarquerAlerteLue = async (id) => {
    try {
      await alertesAPI.marquerLue(id);
      loadData();
    } catch (error) {
      showSnackbar('Erreur', 'error');
    }
  };

  const getStatutChip = (statut) => {
    const configs = {
      'ok': { label: 'Stock OK', color: 'success', icon: <CheckCircle /> },
      'alerte': { label: 'Alerte', color: 'warning', icon: <Warning /> },
      'rupture': { label: 'Rupture', color: 'error', icon: <Error /> }
    };
    const config = configs[statut] || configs['ok'];
    return <Chip size="small" label={config.label} color={config.color} icon={config.icon} />;
  };

  const getTypeMouvementChip = (type) => {
    const configs = {
      'sortie': { label: 'Sortie', color: 'error' },
      'transfert_entree': { label: 'Réception', color: 'success' },
      'transfert_sortie': { label: 'Retour', color: 'warning' }
    };
    const config = configs[type] || { label: type, color: 'default' };
    return <Chip size="small" label={config.label} color={config.color} />;
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }
    <Button
      variant="outlined"
      color="primary"
      startIcon={<CloudUpload />}
      onClick={() => setOpenImportDialog(true)}
      sx={{ ml: 2 }}
    >
      Importer CSV
    </Button>
  return (
    <Container maxWidth="xl">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Mon Stock Personnel</Typography>
      </Box>

      {/* Statistiques */}
      {infoStock && tabValue === 0 && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" variant="body2">
                      Produits en Stock
                    </Typography>
                    <Typography variant="h4">
                      {infoStock.statistiques?.nb_produits || 0}
                    </Typography>
                  </Box>
                  <Inventory color="primary" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" variant="body2">
                      Mes Alertes
                    </Typography>
                    <Typography variant="h4" color="warning.main">
                      {infoStock.statistiques?.nb_alertes || 0}
                    </Typography>
                  </Box>
                  <Warning color="warning" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" variant="body2">
                      Ruptures
                    </Typography>
                    <Typography variant="h4" color="error.main">
                      {infoStock.statistiques?.nb_ruptures || 0}
                    </Typography>
                  </Box>
                  <Error color="error" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Mon Stock" />
          <Tab label="Demander Réapprovisionnement" />
          <Tab 
            label={
              <Badge badgeContent={alertes.length} color="error">
                Mes Alertes
              </Badge>
            } 
          />
          <Tab label="Historique" />
        </Tabs>
      </Paper>

      {/* Onglet Mon Stock */}
      {tabValue === 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>Médicament</strong></TableCell>
                <TableCell><strong>Unité</strong></TableCell>
                <TableCell align="center"><strong>Quantité</strong></TableCell>
                <TableCell align="center"><strong>Seuil</strong></TableCell>
                <TableCell align="center"><strong>Statut</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {monStock.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="text.secondary">
                      Votre stock est vide. Demandez un réapprovisionnement.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                monStock.map((produit) => (
                  <TableRow key={produit.id_medicament} hover>
                    <TableCell>{produit.nom_medicament}</TableCell>
                    <TableCell>{produit.unite}</TableCell>
                    <TableCell align="center">
                      <Typography 
                        variant="h6" 
                        color={produit.statut_stock === 'rupture' ? 'error' : 'inherit'}
                      >
                        {produit.quantite}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{produit.seuil_alerte}</TableCell>
                    <TableCell align="center">{getStatutChip(produit.statut_stock)}</TableCell>
                    <TableCell align="center">
                      <IconButton 
                        color="error" 
                        size="small"
                        onClick={() => handleOpenSortieDialog(produit)}
                        title="Enregistrer une sortie"
                        disabled={produit.quantite === 0}
                      >
                        <Remove />
                      </IconButton>
                      <IconButton 
                        color="primary" 
                        size="small"
                        onClick={() => handleOpenSeuilDialog(produit)}
                        title="Modifier le seuil"
                      >
                        <Settings />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Onglet Demander Réapprovisionnement */}
      {tabValue === 1 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>Médicament</strong></TableCell>
                <TableCell><strong>Unité</strong></TableCell>
                <TableCell align="center"><strong>Dispo Stock Principal</strong></TableCell>
                <TableCell align="center"><strong>Action</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stockPrincipal.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography color="text.secondary">
                      Aucun produit disponible dans le stock principal
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                stockPrincipal.map((produit) => (
                  <TableRow key={produit.id_medicament} hover>
                    <TableCell>{produit.nom_medicament}</TableCell>
                    <TableCell>{produit.unite}</TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={produit.quantite} 
                        color={produit.quantite > 0 ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<Send />}
                        onClick={() => handleOpenTransfertDialog(produit)}
                        disabled={produit.quantite === 0}
                      >
                        Demander
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Onglet Alertes */}
      {tabValue === 2 && (
        <Paper>
          {alertes.length === 0 ? (
            <Box p={3} textAlign="center">
              <CheckCircle color="success" sx={{ fontSize: 60, mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Aucune alerte en cours
              </Typography>
            </Box>
          ) : (
            alertes.map((alerte) => (
              <Alert 
                key={alerte.id_alerte}
                severity={alerte.niveau === 'danger' ? 'error' : 'warning'}
                onClose={() => handleMarquerAlerteLue(alerte.id_alerte)}
                sx={{ mb: 1 }}
              >
                <Typography variant="body2">
                  <strong>{alerte.nom_medicament}</strong> - {alerte.message}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(alerte.date_creation).toLocaleString()}
                </Typography>
              </Alert>
            ))
          )}
        </Paper>
      )}

      {/* Onglet Historique */}
      {tabValue === 3 && (
        <Paper>
          <List>
            {historique.length === 0 ? (
              <Box p={3} textAlign="center">
                <History sx={{ fontSize: 60, mb: 2, color: 'text.secondary' }} />
                <Typography variant="h6" color="text.secondary">
                  Aucun mouvement enregistré
                </Typography>
              </Box>
            ) : (
              historique.map((mouvement, index) => (
                <React.Fragment key={mouvement.id_mouvement}>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body1">
                            {mouvement.nom_medicament}
                          </Typography>
                          {getTypeMouvementChip(mouvement.type_mouvement)}
                          <Chip 
                            label={`${mouvement.type_mouvement.includes('entree') ? '+' : '-'}${mouvement.quantite}`}
                            size="small"
                            color={mouvement.type_mouvement.includes('entree') ? 'success' : 'default'}
                          />
                        </Box>
                      }
                      secondary={
                        <Box mt={0.5}>
                          <Typography variant="body2" color="text.secondary">
                            {mouvement.motif}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(mouvement.date_mouvement).toLocaleString()} • 
                            Stock: {mouvement.quantite_avant} → {mouvement.quantite_apres}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < historique.length - 1 && <Divider />}
                </React.Fragment>
              ))
            )}
          </List>
        </Paper>
      )}

      {/* Dialog Sortie Stock */}
      <Dialog open={openSortieDialog} onClose={handleCloseDialogs} maxWidth="sm" fullWidth>
        <DialogTitle>
          Enregistrer une sortie - {selectedProduit?.nom_medicament}
        </DialogTitle>
        <form onSubmit={handleSortieStock}>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              Stock disponible : <strong>{selectedProduit?.quantite}</strong>
            </Alert>
            <TextField
              name="quantite"
              label="Quantité utilisée"
              type="number"
              fullWidth
              margin="normal"
              value={formData.quantite}
              onChange={handleChange}
              required
              inputProps={{ 
                min: 1, 
                max: selectedProduit?.quantite || 0 
              }}
            />
            <TextField
              name="motif"
              label="Motif (ex: Vaccination chat - Client Dupont)"
              fullWidth
              margin="normal"
              multiline
              rows={2}
              value={formData.motif}
              onChange={handleChange}
              required
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialogs}>Annuler</Button>
            <Button type="submit" variant="contained" color="error">
              Enregistrer
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog Modifier Seuil */}
      <Dialog open={openSeuilDialog} onClose={handleCloseDialogs} maxWidth="sm" fullWidth>
        <DialogTitle>
          Modifier mon seuil d'alerte - {selectedProduit?.nom_medicament}
        </DialogTitle>
        <form onSubmit={handleUpdateSeuil}>
          <DialogContent>
            <TextField
              name="seuil_alerte"
              label="Nouveau seuil d'alerte"
              type="number"
              fullWidth
              margin="normal"
              value={formData.seuil_alerte}
              onChange={handleChange}
              required
              inputProps={{ min: 0 }}
              helperText="Vous serez alerté quand votre stock passe en-dessous de ce seuil"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialogs}>Annuler</Button>
            <Button type="submit" variant="contained">
              Modifier
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog Demander Transfert */}
      <Dialog open={openTransfertDialog} onClose={handleCloseDialogs} maxWidth="sm" fullWidth>
        <DialogTitle>
          Demander un réapprovisionnement - {selectedProduit?.nom_medicament}
        </DialogTitle>
        <form onSubmit={handleDemanderTransfert}>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              Disponible dans le stock principal : <strong>{selectedProduit?.quantite}</strong>
            </Alert>
            <TextField
              name="quantite"
              label="Quantité demandée"
              type="number"
              fullWidth
              margin="normal"
              value={formData.quantite}
              onChange={handleChange}
              required
              inputProps={{ 
                min: 1, 
                max: selectedProduit?.quantite || 0 
              }}
            />
            <TextField
              name="commentaire"
              label="Commentaire (optionnel)"
              fullWidth
              margin="normal"
              multiline
              rows={2}
              value={formData.commentaire}
              onChange={handleChange}
              placeholder="Ex: Besoin pour tournée demain"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialogs}>Annuler</Button>
            <Button type="submit" variant="contained" startIcon={<Send />}>
              Envoyer la demande
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      {/* Dialog Import CSV */}
      <ImportCSVDialog
        open={openImportDialog}
        onClose={() => setOpenImportDialog(false)}
        onSuccess={loadData}
        apiEndpoint={`${process.env.REACT_APP_API_URL || 'http://localhost:3000/api'}/stock/import/technicien`}
        title="Import CSV - Mon Stock Personnel"
      />
    </Container>
  );
};

export default StockTechnicienPage;
